import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  User, 
  Crown, 
  Clock, 
  CheckCircle,
  CheckCircle2,
  XCircle, 
  AlertCircle,
  Phone,
  Calendar,
  Package,
  Edit,
  Save,
  X,
  MessageSquare
} from 'lucide-react';
import toast from 'react-hot-toast';

const UserPanel = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [user, setUser] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [userTickets, setUserTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    username: ''
  });

  useEffect(() => {
    fetchUserProfile();
    fetchPurchases();
    fetchUserTickets();
  }, []);

  const fetchUserTickets = async () => {
    try {
      setIsLoadingTickets(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/tickets?limit=5', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setIsLoadingTickets(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/user/profile');
      setUser(response.data.user);
      setEditForm({
        username: response.data.user.username || ''
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUser(authUser);
      setEditForm({
        username: authUser?.username || ''
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.patch('/api/user/profile', editForm);
      setUser(response.data.user);
      setIsEditing(false);
      toast.success('پروفایل با موفقیت به‌روزرسانی شد');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی پروفایل');
    }
  };

  const handleEditCancel = () => {
    setEditForm({
      username: user?.username || ''
    });
    setIsEditing(false);
  };

  const fetchPurchases = async () => {
    try {
      const response = await axios.get('/api/user/purchases');
      setPurchases(response.data.purchases || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
        icon: Clock,
        text: 'در انتظار بررسی'
      },
      approved: {
        color: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
        icon: CheckCircle,
        text: 'تایید شده (در انتظار تحویل)'
      },
      completed: {
        color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        icon: CheckCircle,
        text: 'تکمیل و تحویل داده شد'
      },
      rejected: {
        color: 'bg-red-500/20 text-red-300 border-red-500/30',
        icon: XCircle,
        text: 'رد شده'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <IconComponent className="w-3.5 h-3.5" />
        {config.text}
      </span>
    );
  };

  const formatDate = (date) => {
    try {
      if (!date) return 'نامشخص';
      let dateObj;
      if (typeof date === 'string') {
        const normalized = date.includes('T') || date.includes('Z') ? date : date.replace(' ', 'T') + 'Z';
        dateObj = new Date(normalized);
      } else {
        dateObj = new Date(date);
      }
      if (isNaN(dateObj.getTime())) return 'نامشخص';
      
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tehran'
      }).format(dateObj);
    } catch (error) {
      return 'نامشخص';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fa-IR').format(price);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050811] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050811] text-white pt-24 pb-12 relative overflow-hidden" dir="rtl">
      
      {/* پس‌زمینه زنده با هاله‌های زمردی و سیان */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-900/20 rounded-full blur-[140px]" />
        <div className="absolute top-[40%] right-0 w-[600px] h-[400px] bg-cyan-900/15 rounded-full blur-[160px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[400px] bg-emerald-950/20 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-emerald-500/20 border border-white/20">
            <User className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-black text-white mb-1">پنل کاربری</h1>
          <p className="text-gray-400 text-sm">
            خوش آمدید، <span className="text-emerald-400 font-bold">{user?.username}</span>
          </p>
        </motion.div>

        {/* Main Box */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white/5 border border-white/10 rounded-3xl shadow-2xl overflow-hidden mb-8 backdrop-blur-xl"
        >
          {/* Nav Tabs */}
          <div className="border-b border-white/10 bg-black/20 p-3">
            <nav className="flex flex-wrap gap-2">
              {[
                { id: 'profile', label: 'پروفایل', icon: User },
                { id: 'purchases', label: 'خریدهای من', icon: Package },
                { id: 'ranks', label: 'رنک‌های من', icon: Crown },
                { id: 'tickets', label: 'تیکت‌های پشتیبانی', icon: MessageSquare }
              ].map((tab) => {
                const IconComponent = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-4 rounded-2xl font-bold text-sm flex items-center gap-2 transition-all ${
                      active
                        ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shadow-md'
                        : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <IconComponent className={`w-4 h-4 ${active ? 'text-emerald-400' : ''}`} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Contents */}
          <div className="p-6 md:p-8">
            
            {/* 1. Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-8">
                {/* Profile Information */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">اطلاعات شخصی</h3>
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        <span>ویرایش</span>
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">نام کاربری در سرور</label>
                        <p className="font-semibold text-white bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm">{user?.gameUsername || 'نامشخص'}</p>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-400 mb-1">نام کاربری</label>
                        <input
                          type="text"
                          value={editForm.username}
                          onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500"
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          type="submit"
                          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          <span>ذخیره</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleEditCancel}
                          className="flex items-center gap-1.5 px-4 py-2 bg-white/10 text-gray-300 rounded-xl text-sm hover:bg-white/20 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>انصراف</span>
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                      <div className="flex items-center justify-between border-b border-white/5 pb-3">
                        <div className="flex items-center gap-3">
                          <User className="w-5 h-5 text-emerald-400" />
                          <span className="text-sm text-gray-400">نام کاربری سرور:</span>
                        </div>
                        <span className="font-bold text-white">{user?.gameUsername || 'نامشخص'}</span>
                      </div>

                      <div className="flex items-center justify-between border-b border-white/5 pb-3">
                        <div className="flex items-center gap-3">
                          <User className="w-5 h-5 text-cyan-400" />
                          <span className="text-sm text-gray-400">نام کاربری سایت:</span>
                        </div>
                        <span className="font-bold text-white">{user?.username || 'نامشخص'}</span>
                      </div>

                      <div className="flex items-center justify-between border-b border-white/5 pb-3">
                        <div className="flex items-center gap-3">
                          <Phone className="w-5 h-5 text-purple-400" />
                          <span className="text-sm text-gray-400">شماره موبایل:</span>
                        </div>
                        <span className="font-bold text-white">{user?.phone && user.phone !== 'ثبت نشده' ? user.phone : 'ثبت نشده'}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-yellow-400" />
                          <span className="text-sm text-gray-400">تاریخ عضویت:</span>
                        </div>
                        <span className="font-bold text-white text-sm">{user?.createdAt ? formatDate(new Date(user.createdAt)) : 'نامشخص'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Account Status Card */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-white">وضعیت حساب</h3>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-7 h-7 text-emerald-400 shrink-0" />
                      <div>
                        <p className="font-bold text-emerald-300">حساب کاربری فعال</p>
                        <p className="text-xs text-emerald-400/80 mt-0.5">شما می‌توانید از تمام امکانات سایت و سرور استفاده کنید</p>
                      </div>
                    </div>
                  </div>

                  {/* Support Quick Banner */}
                  <div className="bg-gradient-to-r from-emerald-950/50 to-cyan-950/50 border border-emerald-500/30 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <MessageSquare className="w-6 h-6 text-emerald-400 shrink-0" />
                      <div>
                        <h4 className="font-bold text-white text-sm">پشتیبانی و تیکت‌ها</h4>
                        <p className="text-xs text-gray-400">سوالی دارید یا نیازمند کمک هستید؟</p>
                      </div>
                    </div>
                    <Link
                      to="/tickets"
                      className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-xl text-center text-sm block hover:opacity-90 transition-opacity"
                    >
                      ورود به بخش تیکت‌ها
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 2. Purchases Tab */}
            {activeTab === 'purchases' && (
              <motion.div variants={itemVariants}>
                <h3 className="text-xl font-bold text-white mb-6">تاریخچه خریدهای من</h3>
                {purchases.length === 0 ? (
                  <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl">
                    <Package className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">هنوز خریدی انجام نداده‌اید</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-right text-sm">
                      <thead className="bg-white/5 text-gray-400 border-b border-white/10">
                        <tr>
                          <th className="px-4 py-3">نوع رنک</th>
                          <th className="px-4 py-3">مبلغ</th>
                          <th className="px-4 py-3">تاریخ خرید</th>
                          <th className="px-4 py-3">وضعیت</th>
                          <th className="px-4 py-3">یادداشت ادمین</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {purchases.map((purchase) => (
                          <tr key={purchase.id} className="hover:bg-white/5 transition-colors">
                            <td className="px-4 py-4 font-bold text-white flex items-center gap-2">
                              <Crown className="w-4 h-4 text-emerald-400" />
                              {purchase.rankType}
                            </td>
                            <td className="px-4 py-4 text-white font-medium">
                              {formatPrice(purchase.amount)} تومان
                            </td>
                            <td className="px-4 py-4 text-gray-400 text-xs">
                              {formatDate(purchase.createdAt || purchase.purchaseDate)}
                            </td>
                            <td className="px-4 py-4">
                              {getStatusBadge(purchase.status)}
                            </td>
                            <td className="px-4 py-4 text-xs text-gray-400">
                              {purchase.adminNotes || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* 3. Active Ranks Tab */}
            {activeTab === 'ranks' && (
              <motion.div variants={itemVariants} className="space-y-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Crown className="w-6 h-6 text-yellow-400" />
                  رنک‌های فعال من
                </h3>

                {purchases.filter(p => p.status === 'completed').length === 0 ? (
                  <div className="text-center py-16 bg-white/5 border border-white/10 rounded-2xl">
                    <Crown className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 font-bold">هنوز رنک ۵۶۷۸فعالی ندارید</p>
                    <p className="text-xs text-gray-500 mt-1">برای خرید رنک به بخش فروشگاه مراجعه کنید</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {purchases.filter(p => p.status === 'completed').map((purchase) => {
                      const activationDate = new Date(purchase.processedAt || purchase.createdAt);
                      const expiryDate = new Date(activationDate);
                      expiryDate.setDate(expiryDate.getDate() + 30);
                      const now = new Date();
                      const daysRemaining = Math.max(0, Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)));
                      const isExpired = daysRemaining === 0;

                      return (
                        <div
                          key={purchase.id}
                          className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3 relative overflow-hidden"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-lg text-white">{purchase.rankType}</span>
                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold border ${
                              isExpired ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                            }`}>
                              {isExpired ? 'منقضی شده' : 'فعال'}
                            </span>
                          </div>
                          <div className="text-xs space-y-1 text-gray-400">
                            <p>نام کاربری: <span className="text-white font-medium">{purchase.gameUsername}</span></p>
                            <p>تاریخ فعال‌سازی: {formatDate(activationDate)}</p>
                            <p>روز باقی‌مانده: <span className="text-emerald-400 font-bold">{daysRemaining} روز</span></p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {/* 4. Tickets Tab */}
            {activeTab === 'tickets' && (
              <motion.div variants={itemVariants} className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <MessageSquare className="w-6 h-6 text-emerald-400" />
                      تیکت‌های پشتیبانی من
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">مشاهده و مدیریت تیکت‌های شما</p>
                  </div>
                  <Link
                    to="/tickets"
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
                  >
                    + ثبت تیکت جدید
                  </Link>
                </div>

                {isLoadingTickets ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-gray-400 text-xs">در حال بارگذاری تیکت‌ها...</p>
                  </div>
                ) : userTickets.length === 0 ? (
                  <div className="text-center py-16 bg-white/5 border border-white/10 rounded-2xl">
                    <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-300 font-bold mb-1">هنوز تیکتی ثبت نکرده‌اید</p>
                    <p className="text-xs text-gray-400 mb-5">در صورت بروز هرگونه مشکل تیکت ارسال کنید</p>
                    <Link
                      to="/tickets"
                      className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-xl text-sm inline-block"
                    >
                      ثبت اولین تیکت
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {userTickets.map(t => (
                      <div
                        key={t.id}
                        onClick={() => navigate(`/tickets/${t.id}`)}
                        className="cursor-pointer bg-white/5 border border-white/10 hover:border-emerald-500/50 rounded-2xl p-4 transition-all flex items-center justify-between gap-4"
                      >
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-500">#{t.id}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                              {t.department === 'technical' ? 'فنی و خرید' : 'مدیریت'}
                            </span>
                          </div>
                          <h4 className="font-bold text-white text-sm hover:text-emerald-300 transition-colors">
                            {t.subject}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">بروزرسانی: {formatDate(t.updatedAt || t.createdAt)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <span className={`text-xs px-3 py-1 rounded-full border ${
                            t.status === 'open' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                            t.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                            t.status === 'waiting_user' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                            t.status === 'waiting_admin' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' :
                            t.status === 'resolved' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                            'bg-gray-500/20 text-gray-400 border-gray-500/30'
                          }`}>
                            {t.status === 'open' ? 'باز' :
                             t.status === 'in_progress' ? 'در بررسی' :
                             t.status === 'waiting_user' ? 'پاسخ پشتیبانی' :
                             t.status === 'waiting_admin' ? 'منتظر پشتیبانی' :
                             t.status === 'resolved' ? 'حل شده' : 'بسته شده'}
                          </span>
                        </div>
                      </div>
                    ))}

                    <div className="text-center pt-2">
                      <Link to="/tickets" className="text-xs text-emerald-400 hover:underline font-bold">
                        مشاهده همه تیکت‌ها
                      </Link>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UserPanel;
